export default function MelbourneDestinationsSection() {
  const stats = [
    {
      value: "58%",
      label: "of Melbourne requests are cruise-related or aerospace travel with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for cruise embarkations from Port Canaveral and tropical surf trips",
    },
    {
      value: "Mar–Aug",
      label: "peak spring break and summer cruise season — passport renewals spike before departures",
    },
    {
      value: "72-hour",
      label: "shortest launch campaign departure window we've accommodated for a Melbourne engineer",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The most common international destination for Melbourne cruise passengers and private boaters. Port Canaveral offers year-round Bahamas cruises on multiple lines, and Tropic Ocean Airways flies direct from MLB to the Bahamas. Recreational boaters from Melbourne and Cocoa Beach make regular runs to the Abacos, Bimini, and Grand Bahama. All passengers need valid passports for entry and re-entry.",
      urgency: "Cruise bookings and private boating trips often come together with 1–3 weeks of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "French Guiana",
      context: "Unique to Melbourne's aerospace community — the Guiana Space Centre in Kourou is the primary launch site for Ariane rockets and a frequent destination for Melbourne engineers from Northrop Grumman and L3Harris supporting satellite payload integration and launch campaigns. French Guiana is an overseas department of France — a valid passport is required, and the six-month validity rule applies.",
      urgency: "Launch campaigns shift constantly — payload integration reviews and launch support trips are often confirmed with 1–3 weeks of notice",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Melbourne families, engineers, and beachside residents regularly cruise from Port Canaveral to the Eastern and Western Caribbean — the Bahamas, Jamaica, the Cayman Islands, Cozumel, St. Thomas, St. Maarten, and beyond. Disney Cruise Line from Port Canaveral is especially popular with Melbourne families. All passengers, including children, need valid passports for most itineraries.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Top surf destination for Melbourne and Satellite Beach surfers chasing Pacific swells. Costa Rica's Pacific coast — Tamarindo, Jaco, Santa Teresa — draws Space Coast surfers during Atlantic hurricane season when storm-generated swells light up Central American breaks. Also popular with Melbourne families for eco-adventure vacations.",
      urgency: "Surf trips are booked on short notice when swell forecasts solidify — often 1–2 weeks before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, Los Cabos, and Puerto Vallarta are staple beach vacation destinations for Melbourne families and couples. Orlando International Airport (MCO), about an hour from Melbourne, offers extensive direct and connecting service to Mexico. Mexico requires six months of passport validity for entry, a rule that catches many travelers.",
      urgency: "Spring break bookings surge in January–February — passport discoveries spike in March for Melbourne families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin draw Melbourne aerospace professionals attending defense program reviews and technical conferences, as well as Space Coast residents taking European vacations. The UK requires passports valid for the duration of your stay; Ireland requires validity through your departure date.",
      urgency: "Conference invitations and program reviews typically arrive 3–6 weeks before departure — tight for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Melbourne Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Melbourne
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Melbourne residents head to the Bahamas and Caribbean for cruises, French Guiana for aerospace launch campaigns, Costa Rica for surf trips, and Europe for defense program reviews and vacations. These destinations create the most passport urgency for our Space Coast clients.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Space Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Cocoa Beach", href: "/cocoa-beach" },
              { name: "Titusville", href: "/titusville" },
              { name: "Orlando", href: "/orlando" },
              { name: "Daytona Beach", href: "/daytona-beach" },
              { name: "Stuart", href: "/stuart" },
              { name: "Port St. Lucie", href: "/port-st-lucie" },
              { name: "West Palm Beach", href: "/west-palm-beach" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}